import { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import BaseModal from '../Base/BaseModal';
import './css/BookingModal.css'
import { BOOKING_STATUS, TABLE_STATUS, MODAL_MODES } from '../../../../constants/enums';
import { formatISOStringToReadable } from '../../../../utils/dateUtils';

export default function BookingModal({
  isOpen,
  onClose,
  table,
  booking,
  onUpdateTableStatus,
  onUpdateBookingStatus,
}) {
  const { t } = useTranslation();
  const [selectedTableStatus, setSelectedTableStatus] = useState('available');
  const [selectedBookingStatus, setSelectedBookingStatus] = useState('confirmed');

  // Update initial status values when table or booking changes
  useEffect(() => {
    if (table) {
      setSelectedTableStatus(table.status || 'available');
    }
    if (booking) {
      setSelectedBookingStatus(booking.status || 'confirmed');
    }
  }, [table, booking]);

  // Prevent body scroll when modal is open
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }

    // Cleanup function to restore scroll when component unmounts
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isOpen]);

  if (!isOpen || !table) return null;

  const getStatusBadgeClass = (status) => {
    const normalizedStatus = status?.toUpperCase();
    switch (normalizedStatus) {
      case TABLE_STATUS.AVAILABLE:
        return 'status-badge status-available';
      case TABLE_STATUS.IN_SERVICE:
        return 'status-badge status-in-service';
      case TABLE_STATUS.OUT_OF_SERVICE:
        return 'status-badge status-out-of-service';
      case BOOKING_STATUS.PENDING:
        return 'status-badge status-pending';
      case BOOKING_STATUS.CONFIRMED:
        return 'status-badge status-confirmed';
      case BOOKING_STATUS.COMPLETED:
        return 'status-badge status-completed';
      case BOOKING_STATUS.CANCELLED:
        return 'status-badge status-cancelled';
      default:
        return 'status-badge';
    }
  };

  const handleUpdateTableStatus = () => {
    onUpdateTableStatus(table, selectedTableStatus);
    onClose();
  };

  const handleUpdateBookingStatus = () => {
    if (booking) {
      onUpdateBookingStatus(booking, selectedBookingStatus);
      onClose();
    }
  };

  const renderContent = () => (
    <>
      {/* Table Information Section */}
      <div className="detail-section">
        <h3>{t('admin.table_management.modal.table_info')}</h3>
        <div className="detail-grid">
          <div className="detail-item">
            <label>{t('admin.table_management.modal.table_name')}:</label>
            <span>{table.tableName}</span>
          </div>
          <div className="detail-item">
            <label>{t('admin.table_management.modal.capacity')}:</label>
            <span>{table.capacity}</span>
          </div>
        </div>
        <div className="detail-grid">
          <div className="detail-item">
            <label>{t('admin.table_management.modal.status')}:</label>
            <span className={getStatusBadgeClass(table.status)}>
              {t(`status.table.${table.status.toLowerCase()}`)}
            </span>
          </div>
        </div>
      </div>

      {/* Booking Information Section */}
      {booking ? (
        <div className="detail-section">
          <h3>{t('admin.table_management.modal.booking_info')}</h3>
          <div className="detail-grid">
            <div className="detail-item">
              <label>{t('admin.table_management.modal.customer')}:</label>
              <span>{booking.customer?.customerName || t('booking_details.na')}</span>
            </div>
            <div className="detail-item">
              <label>{t('admin.table_management.modal.phone')}:</label>
              <span>{booking.customer?.phoneNumber || t('booking_details.na')}</span>
            </div>
          </div>
          <div className="detail-grid">
            <div className="detail-item">
              <label>{t('admin.table_management.modal.time')}:</label>
              <span>{formatISOStringToReadable(booking.bookingAt)}</span>
            </div>
            <div className="detail-item">
              <label>{t('admin.table_management.modal.guests')}:</label>
              <span>{booking.numGuests}</span>
            </div>
          </div>
          <div className="detail-grid">
            <div className="detail-item">
              <label>{t('admin.table_management.modal.note')}:</label>
              <span>{booking.note || t('booking_details.na')}</span>
            </div>
            <div className="detail-item">
              <label>{t('admin.table_management.modal.status')}:</label>
              <span className={getStatusBadgeClass(booking.status)}>
                {t(`status.booking.${booking.status.toLowerCase()}`)}
              </span>
            </div>
          </div>
        </div>
      ) : (
        <div className="detail-section">
          <div className="no-booking-message">
            <p>{t('admin.table_management.modal.no_booking')}</p>
          </div>
        </div>
      )}

      {/* Admin Controls Section */}
      <div className="detail-section">
        <h3>{t('admin.table_management.modal.admin_controls')}</h3>
        <div className="form-grid">
          <div className="form-item">
            <label>{t('admin.table_management.modal.update_table')}:</label>
            <div className="control-actions">
              <select 
                className="form-input"
                value={selectedTableStatus} 
                onChange={(e) => setSelectedTableStatus(e.target.value)}
              >
                <option value={TABLE_STATUS.AVAILABLE}>
                  {t(`status.table.available`)}
                </option>
                <option value={TABLE_STATUS.IN_SERVICE}>
                  {t(`status.table.in_service`)}
                </option>
                <option value={TABLE_STATUS.OUT_OF_SERVICE}>
                  {t(`status.table.out_of_service`)}
                </option>
              </select>
              <button className="btn btn-primary" onClick={handleUpdateTableStatus}>
                {t('admin.table_management.modal.update_table')}
              </button>
            </div>
          </div>

          {booking && (
            <div className="form-item">
              <label>{t('admin.table_management.modal.update_booking')}:</label>
              <div className="control-actions">
                <select 
                  className="form-input"
                  value={selectedBookingStatus} 
                  onChange={(e) => setSelectedBookingStatus(e.target.value)}
                >
                  <option value={BOOKING_STATUS.PENDING}>
                    {t('status.booking.pending')}
                  </option>
                  <option value={BOOKING_STATUS.CONFIRMED}>
                    {t('status.booking.confirmed')}
                  </option>
                  <option value={BOOKING_STATUS.COMPLETED}>
                    {t('status.booking.completed')}
                  </option>
                  <option value={BOOKING_STATUS.CANCELLED}>
                    {t('status.booking.cancelled')}
                  </option>
                </select>
                <button className="btn btn-primary" onClick={handleUpdateBookingStatus}>
                  {t('admin.table_management.modal.update_booking')}
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </>
  );

  const modalContent = (
    <BaseModal
      title={`${t('admin.table_management.modal.table_info')} ${table.tableId}`}
      mode={MODAL_MODES.VIEW}
      onClose={onClose}
      onSave={null}
      onDelete={null}
      validationErrors={[]}
    >
      {renderContent()}
    </BaseModal>
  );

  // Use createPortal to render the modal outside of the table-map-container
  return createPortal(modalContent, document.body);
}